import { BaseViewModel } from "@iot-platform/core";
import type { GraphFilters } from "@station/domain/models/graph.model";
import type {
    EffectiveRange,
    Sensor,
} from "@station/domain/models/sensor.model";
import { FormatEnum } from "@station/domain/models/sensor.model";
import { GraphsGetByUseCase } from "@station/domain/usecases/graph-get-by.usecase";
import {
    formatSeriesLabel,
    getSensorLabel,
    getUniqueSensorLabels,
} from "@station/domain/utils/sensor-label.utils";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

interface Point {
    name: string;
    yAxisValues: Record<string, number>;
}

interface Params {
    stationId: number;
    sensorIds: number[];
    from: number;
    to: number;
    sensors: Readonly<Sensor>[];
}

const MS_PER_SECOND = 1000;

@injectable()
export class CustomGraphChartViewmodel extends BaseViewModel {
    @observable
    initialLoading = true;

    @observable
    graphs: Point[] = [];

    @observable
    initialLines: Record<string, boolean> = {};

    @observable
    units: Record<string, string> = {};

    @observable
    seriesColor: Record<string, string> = {};

    @observable
    effectiveRanges: Record<string, EffectiveRange | null> = {};

    @observable yAxesFormatted: {
        id: number;
        label: string;
        variableIds: string[];
    }[] = [];

    private params?: Params;

    private sensorLabels = new Map<number, string>();

    constructor(
        @inject(GraphsGetByUseCase)
        private readonly graphsGetByUseCase: GraphsGetByUseCase,
    ) {
        super();
        makeObservable(this);
    }

    private labelOf(sensor: Readonly<Sensor>): string {
        return this.sensorLabels.get(sensor.id) ?? getSensorLabel(sensor);
    }

    @action
    setParams(params: Params): void {
        this.params = params;
        this.sensorLabels = getUniqueSensorLabels(params.sensors);

        this.units = params.sensors.reduce<Record<string, string>>(
            (acc, sensor) => {
                if (sensor.symbol)
                    acc[formatSeriesLabel(sensor, this.labelOf(sensor))] =
                        sensor.symbol;
                return acc;
            },
            {},
        );

        this.seriesColor = params.sensors.reduce<Record<string, string>>(
            (acc, sensor) => {
                acc[formatSeriesLabel(sensor, this.labelOf(sensor))] =
                    sensor.color;
                return acc;
            },
            {},
        );

        this.effectiveRanges = params.sensors.reduce<
            Record<string, EffectiveRange | null>
        >((acc, sensor) => {
            acc[formatSeriesLabel(sensor, this.labelOf(sensor))] =
                sensor.effectiveRange;
            return acc;
        }, {});

        void this.loadGraphData();
    }

    private setInitialLines(values: Record<string, number>): void {
        this.initialLines = Object.keys(values).reduce<Record<string, boolean>>(
            (acc, k) => {
                acc[k] = true;
                return acc;
            },
            {},
        );
    }

    private async loadGraphData(): Promise<void> {
        if (!this.params) return;
        this.initialLoading = true;

        const filter: GraphFilters = {
            stationId: this.params.stationId,
            sensorIds: this.params.sensorIds,
            from: this.params.from,
            to: this.params.to,
        };

        const graphs = await this.graphsGetByUseCase.execute(filter);

        runInAction(() => {
            this.graphs = graphs.map((graph) => ({
                name: DateTime.fromMillis(
                    Number(graph.name) * MS_PER_SECOND,
                ).toFormat("dd-MM-yyyy HH:mm"),
                yAxisValues: Object.fromEntries(
                    Object.entries(graph.yAxisValues)
                        .filter(([sensorId]) => {
                            const sensor = this.params?.sensors.find(
                                (sensorValue) =>
                                    sensorValue.id === Number(sensorId),
                            );
                            return sensor?.format !== FormatEnum.unix_timestamp;
                        })
                        .map(([sensorId, value]) => {
                            const sensor = this.params?.sensors.find(
                                (sensorValue) =>
                                    sensorValue.id === Number(sensorId),
                            );
                            const label = sensor
                                ? formatSeriesLabel(
                                      sensor,
                                      this.labelOf(sensor),
                                  )
                                : sensorId;
                            return [
                                label,
                                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                                parseFloat(value.toFixed(2)),
                            ];
                        }),
                ),
            }));

            const first = this.graphs[0]?.yAxisValues ?? {};
            this.setInitialLines(first);

            const lineKeys = Object.keys(this.graphs[0]?.yAxisValues ?? {});
            this.yAxesFormatted = lineKeys.map((label, idx) => ({
                id: idx,
                label,
                variableIds: [label],
            }));

            this.initialLoading = false;
        });
    }
}
